<template>
  <div class="replies-view">
    <div class="flex justify-between items-center mb-4">
      <h1 class="text-2xl font-semibold">Email Replies</h1>
      
      <div v-if="originalEmailId" class="flex items-center">
        <span class="text-sm text-gray-600 mr-2">Showing replies for email:</span>
        <span class="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">{{ originalEmailId }}</span>
        <button @click="originalEmailId = null; loadReplies()" class="ml-2 text-sm text-blue-600 hover:text-blue-800">
          Show All Replies
        </button>
      </div>
    </div>
    
    <!-- Replies list -->
    <div class="replies-list">
      <div v-if="loading" class="text-center py-4">
        Loading replies...
      </div>
      
      <div v-else-if="!replies.length" class="text-center py-4 text-gray-500">
        No replies found.
      </div>
      
      <div v-else class="space-y-4">
        <div v-for="reply in replies" :key="reply.id" 
          class="bg-white shadow rounded-lg p-4 border border-gray-200"
        >
          <div class="flex justify-between items-start">
            <div>
              <h3 class="font-medium text-lg">{{ reply.subject || 'No Subject' }}</h3>
              <p class="text-sm text-gray-600">
                <strong>From:</strong> {{ reply.from_email }}<br>
                <strong>Received:</strong> {{ formatDate(reply.received_at) }}
              </p>
            </div>
            <div class="flex space-x-2">
              <button @click="toggleReplyContent(reply.id)" 
                class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm">
                {{ expandedReplies.includes(reply.id) ? 'Hide reply' : 'Show reply' }}
              </button>
              <router-link :to="{ path: '/admin/outcomes', query: { reply_id: reply.id, title: reply.subject, on: String(reply.received_at).slice(0, 10) } }"
                class="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm">
                Log outcome
              </router-link>
              <button @click="subscribe(reply)"
                class="px-3 py-1 border border-gray-300 rounded text-sm bg-white text-gray-700 hover:bg-gray-50"
                title="They asked to hear from us: add them to the updates list">
                Add to updates list
              </button>
              <select class="px-2 py-1 border border-gray-300 rounded text-sm bg-white text-gray-700"
                title="Record how this person responded"
                @change="setStatus(reply, ($event.target as HTMLSelectElement))">
                <option value="">Sender status…</option>
                <option value="interested">Interested</option>
                <option value="redirected">Redirected us</option>
                <option value="not_in_remit">Not their remit</option>
                <option value="opted_out">Opted out</option>
              </select>
              <button @click="deleteReply(reply.id)"
                class="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm">
                Delete
              </button>
            </div>
          </div>
          
          <div v-if="expandedReplies.includes(reply.id)" class="mt-4 bg-gray-50 p-4 rounded whitespace-pre-wrap text-gray-700">
            {{ reply.body || 'No content' }}
          </div>
          <div v-else class="mt-4 bg-gray-50 p-4 rounded text-gray-700">
            {{ truncateText(reply.body) || 'No content' }}
          </div>
          
          <ReplyAttachments :attachments="reply.attachments" />

          <div class="mt-2 text-sm text-gray-500">
            <span>In reply to: {{ reply.original_email_id }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import apiClient from '@/api/client'
import { formatDate } from '@/utils/date'
import ReplyAttachments, { type ReplyAttachment } from '@/components/email/ReplyAttachments.vue'
import { trackingApi } from '@/api/tracking'

interface Reply {
  id: string
  from_email: string
  subject: string
  body: string
  received_at: string
  original_email_id: string
  message_id: string
  references_id: string
  has_attachments?: boolean
  attachments?: ReplyAttachment[]
}

const route = useRoute()
const replies = ref<Reply[]>([])
const loading = ref(true)
const expandedReplies = ref<string[]>([])
const originalEmailId = ref<string | null>(null)

// Load replies
const loadReplies = async () => {
  try {
    loading.value = true
    expandedReplies.value = []
    
    // Add email_id filter if specified
    const url = originalEmailId.value 
      ? `/api/emails/replies?email_id=${originalEmailId.value}` 
      : '/api/emails/replies'
      
    const response = await apiClient.get(url)
    
    // Ensure we have a valid response
    if (!response || !response.data) {
      throw new Error('Invalid response from server')
    }
    
    // Handle both array and object responses
    const replyData = Array.isArray(response.data) ? response.data : 
                     (response.data.replies || response.data.data || [])
    
    // Filter and validate replies
    replies.value = replyData.filter((reply: Reply) => {
      if (!reply) return false
      
      // Check for required fields
      const hasRequiredFields = reply.from_email && reply.received_at
      
      // Check for content
      const hasContent = reply.body
      
      return hasRequiredFields && hasContent
    })
  } catch (error: any) {
    console.error('Error loading replies:', error)
  } finally {
    loading.value = false
  }
}

// Watch for route changes to update email_id filter
watch(() => route.query.email_id, (newEmailId) => {
  if (newEmailId) {
    originalEmailId.value = newEmailId as string
  } else {
    originalEmailId.value = null
  }
  loadReplies()
}, { immediate: true })

// Toggle reply content visibility
const toggleReplyContent = (replyId: string) => {
  console.log('Toggling reply content for:', replyId)
  console.log('Before:', expandedReplies.value)
  
  if (expandedReplies.value.includes(replyId)) {
    expandedReplies.value = expandedReplies.value.filter(id => id !== replyId)
  } else {
    expandedReplies.value.push(replyId)
  }
  
  console.log('After:', expandedReplies.value)
}

// They asked for our updates; this reply is the evidence
const subscribe = async (reply: Reply) => {
  try {
    const { data } = await trackingApi.addSubscriber({ email: reply.from_email, source_reply_id: reply.id })
    alert(data.already_on_the_list ? 'Already on the updates list.' : 'Added to the updates list.')
  } catch (error: any) {
    alert(error.response?.data?.detail || 'Could not add them to the list')
  }
}

// Record how the sender responded, with this reply as the evidence
const setStatus = async (reply: Reply, select: HTMLSelectElement) => {
  const status = select.value
  if (!status) return
  const note = status === 'redirected' ? window.prompt('Who did they send us to?') : ''
  if (note === null) {
    select.value = ''
    return
  }
  try {
    await trackingApi.setContactStatus({ email: reply.from_email, status, note: note || '', source_reply_id: reply.id })
    alert('Saved.')
  } catch (error: any) {
    alert(error.response?.data?.detail || 'Could not save the status')
  } finally {
    select.value = ''
  }
}

// Delete a reply
const deleteReply = async (replyId: string) => {
  if (!confirm('Are you sure you want to delete this reply?')) return
  
  try {
    await apiClient.delete(`/api/emails/replies/${replyId}`)
    replies.value = replies.value.filter(reply => reply.id !== replyId)
  } catch (error: any) {
    console.error('Error deleting reply:', error)
    alert('Failed to delete reply')
  }
}

// Truncate text for preview
const truncateText = (text: string | undefined, maxLength = 100) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

onMounted(loadReplies)
</script>

<style scoped>
.replies-view {
  padding: 20px;
}

.reply-body {
  white-space: pre-wrap;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
}
</style> 